import type { FirebaseApp } from 'firebase/app';
import { doc, getDoc, type Firestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, httpsCallable, type Functions } from 'firebase/functions';

import type { EmulatorEndpoint } from '@taki/runtime-config';
import { VUE_HTML_BROUILLONS, type VueHtmlBrouillon } from '@taki/data-model';

/**
 * LES VUES HTML D'UNE FICHE, COTE ECRAN (`docs/socle/vues-html.md`).
 *
 * Le MCP les depose (`vue_proposer` ecrit un brouillon d'une heure, `vue_publier`
 * le publie). L'ecran en fait deux choses, et rien d'autre :
 *
 * - `lireBrouillon(id)` : l'APERCU (`?apercu=<id>` dans l'adresse) lit le
 *   brouillon, que les rules ne laissent lire qu'a son auteur, tant qu'il n'a
 *   pas expire. Absent, expire, d'un autre : `null`, jamais une erreur a
 *   afficher telle quelle ;
 * - `publier(id)` : le bouton « Publier » de l'apercu. La callable
 *   `publierVueHtml` refait TOUS les controles de `vue_publier` (auteur, non
 *   expire, administrateur ou proprietaire, champs et liens) et ecrit par le
 *   meme chemin que le Studio (`functions/src/ia/vues-html.ts`).
 *
 * Le client n'ecrit jamais un brouillon ni une declaration lui-meme.
 */

/** DOIT valoir `FUNCTIONS_REGION` de `functions/src/runtime.ts`. */
export const VUES_HTML_FUNCTIONS_REGION = 'europe-west1';
/** `CALLABLE_PUBLIER_VUE_HTML` de `functions/src/ia/vues-html.ts`. */
export const CALLABLE_PUBLIER_VUE_HTML = 'publierVueHtml';

/** Ce que rend une publication : la vue, sa version, son app. */
export interface VueHtmlPubliee {
  appId: string;
  viewId: string;
  version: number;
}

export interface VuesHtmlClient {
  lireBrouillon(id: string): Promise<VueHtmlBrouillon | null>;
  publier(id: string): Promise<VueHtmlPubliee>;
}

const ID_BROUILLON = /^[A-Za-z0-9_-]{1,128}$/;
const emulatorBoundFunctions = new WeakSet<Functions>();

function date(valeur: unknown): Date | null {
  return valeur != null && typeof valeur === 'object' && 'toDate' in valeur && typeof valeur.toDate === 'function' ? valeur.toDate() as Date : null;
}
function chaines(valeur: unknown): string[] {
  return Array.isArray(valeur) ? valeur.filter((item): item is string => typeof item === 'string') : [];
}

/** Un document de brouillon lu, ramene au contrat ; `null` s'il n'en a pas la forme. */
export function lireDocumentBrouillon(id: string, donnees: Record<string, unknown> | undefined): VueHtmlBrouillon | null {
  if (!donnees) return null;
  const jeu = donnees['jeu'] as { family?: unknown; type?: unknown } | undefined;
  const texte = (clef: string) => (typeof donnees[clef] === 'string' ? donnees[clef] as string : null);
  const appId = texte('appId'), porte = texte('porte'), viewId = texte('viewId'), label = texte('label'), html = texte('html'), uid = texte('uid');
  if (!appId || !porte || !viewId || !label || html === null || !uid || typeof jeu?.family !== 'string') return null;
  const liens = chaines(donnees['liens']);
  return {
    id, appId, porte, viewId, label, html, uid,
    jeu: { family: jeu.family, ...(typeof jeu.type === 'string' ? { type: jeu.type } : {}) },
    champs: chaines(donnees['champs']),
    ...(Array.isArray(donnees['liens']) ? { liens } : {}),
    recordId: texte('recordId'),
    expiresAt: date(donnees['expiresAt']),
  };
}

function codeDe(erreur: unknown): string {
  return erreur != null && typeof erreur === 'object' && typeof (erreur as { code?: unknown }).code === 'string' ? (erreur as { code: string }).code : '';
}

export function createVuesHtmlClient(db: Firestore, orgId: string, app: FirebaseApp, emulator?: EmulatorEndpoint | undefined): VuesHtmlClient {
  let appeler: ReturnType<typeof httpsCallable<{ brouillonId: string }, VueHtmlPubliee>> | null = null;
  return {
    async lireBrouillon(id) {
      if (!ID_BROUILLON.test(id)) return null;
      try {
        const snap = await getDoc(doc(db, 'orgs', orgId, VUE_HTML_BROUILLONS, id));
        const brouillon = lireDocumentBrouillon(snap.id, snap.data());
        if (brouillon?.expiresAt && brouillon.expiresAt.getTime() <= Date.now()) return null;
        return brouillon;
      } catch (erreur) {
        // Absent, expire ou d'un autre auteur : les rules refusent la lecture.
        if (codeDe(erreur) === 'permission-denied' || codeDe(erreur) === 'not-found') return null;
        throw erreur;
      }
    },
    async publier(id) {
      if (!ID_BROUILLON.test(id)) throw new Error('Brouillon de vue invalide.');
      if (appeler === null) {
        const functions = getFunctions(app, VUES_HTML_FUNCTIONS_REGION);
        if (emulator && !emulatorBoundFunctions.has(functions)) {
          connectFunctionsEmulator(functions, emulator.host, emulator.port);
          emulatorBoundFunctions.add(functions);
        }
        appeler = httpsCallable<{ brouillonId: string }, VueHtmlPubliee>(functions, CALLABLE_PUBLIER_VUE_HTML);
      }
      return (await appeler({ brouillonId: id })).data;
    },
  };
}
